"use client";

import { quoteThemeColorStyles, quoteThemeCssVars } from "../quote-theme/quote-theme-css-vars";
import { useState } from 'react';
import { Star, Send, CheckCircle2 } from 'lucide-react';

interface FeedbackSectionProps {
  onSubmitFeedback?: (feedback: {
    rating: number;
    comment: string;
  }) => Promise<void>;
  primaryColor?: string; // legacy, theme CSS vars used
}

export function FeedbackSection({ 
  onSubmitFeedback,
  primaryColor = '#3B82F6'
}: FeedbackSectionProps) {
  const [rating, setRating] = useState<number>(0);
  const [hoverRating, setHoverRating] = useState<number>(0);
  const [comment, setComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!rating) return;

    setIsSubmitting(true);
    try {
      await onSubmitFeedback?.({ rating, comment });
      setIsSubmitted(true);
    } catch (error) {
      console.error('Failed to submit feedback:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="bg-white rounded-3xl shadow-sm p-6">
        <div className="flex flex-col items-center justify-center space-y-4 py-8">
          <CheckCircle2 
            className="w-12 h-12" 
            style={quoteThemeColorStyles.primary}
          />
          <h3 className="text-xl font-semibold text-gray-900">Thank You!</h3>
          <p className="text-gray-600 text-center">
            Your feedback has been submitted and will help us improve our service.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-3xl shadow-sm p-6">
      <h2 className="text-2xl font-semibold mb-6">Your Feedback</h2>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Rating Stars */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">
            How would you rate this quote?
          </label>
          <div className="flex gap-2">
            {[1, 2, 3, 4, 5].map((star) => (
              <button
                key={star}
                type="button"
                onClick={() => setRating(star)}
                onMouseEnter={() => setHoverRating(star)}
                onMouseLeave={() => setHoverRating(0)}
                className="focus:outline-none"
              >
                <Star
                  className={`w-8 h-8 transition-colors`}
                  style={{
                    fill: star <= (hoverRating || rating) ? quoteThemeCssVars.primary : 'transparent',
                    color: star <= (hoverRating || rating) ? quoteThemeCssVars.primary : '#D1D5DB'
                  }}
                />
              </button>
            ))}
          </div>
          {rating > 0 && (
            <p className="text-sm" style={quoteThemeColorStyles.primary}>
              {rating === 5
                ? "Excellent! We're glad you love it!"
                : rating === 4
                ? "Great! Thanks for the positive feedback."
                : rating === 3
                ? "Thanks for your feedback. How can we improve?"
                : "We're sorry you're not satisfied. Please let us know why."}
            </p>
          )}
        </div>

        {/* Comment Box */}
        <div className="space-y-2">
          <label
            htmlFor="comment"
            className="block text-sm font-medium text-gray-700"
          >
            Additional Comments
          </label>
          <textarea
            id="comment"
            rows={4}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-xl 
                     text-sm shadow-sm placeholder-gray-400
                     focus:outline-none focus:ring-1
                     disabled:bg-gray-50 disabled:text-gray-500"
            style={{
              borderColor: comment ? quoteThemeCssVars.primary : undefined,
              boxShadow: comment ? `0 0 0 1px ${quoteThemeCssVars.primary}` : undefined
            }}
            placeholder="Tell us what you think about this quote..."
          />
        </div>

        {/* Submit Button */}
        <button
          type="submit"
          disabled={!rating || isSubmitting}
          className="flex items-center justify-center gap-2 w-full px-4 py-2 
                   rounded-xl font-medium
                   hover:brightness-90 focus:outline-none focus:ring-2 
                   focus:ring-offset-2 transition-colors
                   disabled:opacity-50 disabled:cursor-not-allowed"
          style={{ 
            backgroundColor: quoteThemeCssVars.primary,
            color: 'white'
          }}
        >
          {isSubmitting ? (
            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
          ) : (
            <>
              <Send className="w-4 h-4" />
              Submit Feedback
            </>
          )}
        </button>
      </form>
    </div>
  );
} 